import React, { useCallback, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { format } from 'date-fns';
import { subDays } from 'date-fns';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import DateRangePicker from '../../components/fields/DateRangePicker';
import PaginationComponent from '../../components/PaginationComponent';
import { revenueThresholdAPI } from '../../services';
import { useBranch } from '../../context/BranchContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(
    Number(value || 0),
  );

const formatNumber = (value) =>
  new Intl.NumberFormat('vi-VN').format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
};

const PAYMENT_METHOD_OPTIONS = [
  { value: 'CASH', label: 'Tiền mặt' },
  { value: 'BANK_TRANSFER', label: 'Chuyển khoản' },
];

const PAYMENT_METHOD_LABELS = { CASH: 'Tiền mặt', BANK_TRANSFER: 'Chuyển khoản' };

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------

const SummaryCard = ({ title, value, subtitle, variant = 'primary' }) => (
  <Card className={`border-${variant} h-100`}>
    <Card.Body>
      <div className={`text-${variant} small fw-semibold mb-1`}>{title}</div>
      <div className="fs-5 fw-bold">{value}</div>
      {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
    </Card.Body>
  </Card>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const getDefaultDates = () => ({
  startDate: format(subDays(new Date(), 29), 'yyyy-MM-dd'),
  endDate: format(new Date(), 'yyyy-MM-dd'),
});

export default function RevenueThresholdPage() {
  // #175019 — chi nhánh chỉ chọn ở thanh trên đầu; trang không còn ô lọc riêng.
  const { selectedBranch } = useBranch();

  const defaultDates = getDefaultDates();

  // Form state
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [targetAmount, setTargetAmount] = useState('');
  const [tolerance, setTolerance] = useState('200000');
  const branchId = selectedBranch?.id || '';

  // Calculation state
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);

  // Orders table state
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLimit] = useState(20);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  // ---------------------------------------------------------------------------

  const buildPayload = useCallback(() => ({
    startDate,
    endDate,
    paymentMethod,
    targetAmount: Number(targetAmount),
    tolerance: Number(tolerance) || 200000,
    ...(branchId ? { branchId } : {}),
  }), [startDate, endDate, paymentMethod, targetAmount, tolerance, branchId]);

  const handleCalculate = async () => {
    if (!targetAmount || Number(targetAmount) <= 0) {
      toast.warning('Vui lòng nhập doanh thu mục tiêu hợp lệ.');
      return;
    }
    if (!startDate || !endDate) {
      toast.warning('Vui lòng chọn khoảng thời gian.');
      return;
    }

    setCalculating(true);
    setResult(null);
    setOrders([]);
    setOrdersTotal(0);
    setOrdersPage(1);

    try {
      // Axios interceptor unwraps res.data, so the result is the plain DTO object
      const result = await revenueThresholdAPI.calculate(buildPayload());
      setResult(result);
      if (result?.threshold > 0) {
        await loadOrders(result.threshold, 1);
      }
    } catch (err) {
      toast.error(err?.message || 'Có lỗi khi tính toán.');
    } finally {
      setCalculating(false);
    }
  };

  const loadOrders = async (threshold, page) => {
    setLoadingOrders(true);
    try {
      const params = {
        ...buildPayload(),
        threshold,
        page,
        limit: ordersLimit,
      };
      // Axios interceptor unwraps res.data → { data: [], total, page, limit }
      const res = await revenueThresholdAPI.getOrders(params);
      setOrders(Array.isArray(res?.data) ? res.data : []);
      setOrdersTotal(res?.total || 0);
      setOrdersPage(page);
    } catch (err) {
      toast.error(err?.message || 'Không tải được danh sách đơn.');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handlePageChange = (page) => {
    if (result?.threshold >= 0) {
      loadOrders(result.threshold, page);
    }
  };

  const handleExportCsv = async () => {
    if (!result) return;
    setExportingCsv(true);
    try {
      const params = {
        ...buildPayload(),
        threshold: result.threshold,
      };
      const blob = await revenueThresholdAPI.exportCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nguong-doanh-thu_${startDate}_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Đã xuất file CSV.');
    } catch (err) {
      toast.error(err?.message || 'Xuất CSV thất bại.');
    } finally {
      setExportingCsv(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived display values
  // ---------------------------------------------------------------------------

  const differenceColor = result
    ? result.differenceFromTarget >= 0 ? 'success' : 'danger'
    : 'secondary';

  const differenceLabel = result
    ? `${result.differenceFromTarget >= 0 ? '+' : ''}${formatCurrency(result.differenceFromTarget)}`
    : '-';

  // ---------------------------------------------------------------------------

  return (
    <PageLayout>
      <div className="mc-page">
        {/* Header */}
        <div className="mc-page-header d-flex align-items-center justify-content-between mb-4">
          <div>
            <h1 className="mc-page-title mb-1">Tính ngưỡng doanh thu</h1>
            <p className="text-muted small mb-0">
              Tìm mức tiền đơn tối đa để đạt doanh thu mục tiêu trong kỳ chọn.
            </p>
          </div>
        </div>

        {/* Filter card */}
        <Card className="mb-4">
          <Card.Body className="pb-3">
            {/* Row 1: Date range (full width — needs room for preset buttons) */}
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Label className="fw-semibold small text-uppercase text-muted mb-2">
                  Khoảng thời gian
                </Form.Label>
                <DateRangePicker
                  fromDate={startDate}
                  toDate={endDate}
                  onChange={(from, to) => {
                    setStartDate(from);
                    setEndDate(to);
                  }}
                  defaultPreset="last30days"
                />
              </Col>
            </Row>

            {/* Divider */}
            <hr className="my-3" />

            {/* Row 2: Business params + submit */}
            <Row className="g-3 align-items-end">
              {/* Payment method */}
              <Col xs={6} sm={4} md={3} lg={2}>
                <Form.Label className="fw-semibold small text-uppercase text-muted mb-1">
                  Phương thức TT
                </Form.Label>
                <Form.Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  {PAYMENT_METHOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* Target amount */}
              <Col xs={6} sm={4} md={3} lg={2}>
                <Form.Label className="fw-semibold small text-uppercase text-muted mb-1">
                  Mục tiêu / tháng
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    min={0}
                    step={100000}
                    placeholder="10,000,000"
                    value={targetAmount}
                    onChange={(e) => setTargetAmount(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCalculate()}
                  />
                  <span className="input-group-text text-muted small">₫</span>
                </div>
              </Col>

              {/* Tolerance */}
              <Col xs={6} sm={4} md={3} lg={2}>
                <Form.Label className="fw-semibold small text-uppercase text-muted mb-1">
                  Biên độ / tháng
                </Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="number"
                    min={0}
                    step={50000}
                    value={tolerance}
                    onChange={(e) => setTolerance(e.target.value)}
                  />
                  <span className="input-group-text text-muted small">₫</span>
                </div>
                <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: 2 }}>
                  Cho phép vượt tối đa / tháng
                </div>
              </Col>

              {/* Submit */}
              <Col xs={12} sm="auto" className="ms-sm-auto">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-100 px-4"
                  onClick={handleCalculate}
                  disabled={calculating}
                >
                  {calculating ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Đang tính...
                    </>
                  ) : (
                    'Tính toán'
                  )}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Results */}
        {result && (
          <>
            {/* Note */}
            {result.note && (
              <div className="alert alert-warning py-2 mb-3">
                <small>{result.note}</small>
              </div>
            )}

            {/* Period info banner */}
            <Card className="mb-3 border-0 bg-light">
              <Card.Body className="py-2 px-3">
                <div className="d-flex flex-wrap gap-3 align-items-center small text-muted">
                  <span>
                    <strong className="text-dark">{startDate}</strong>
                    {' → '}
                    <strong className="text-dark">{endDate}</strong>
                    <span className="ms-1">({result.periodDays} ngày)</span>
                  </span>
                  <span className="vr d-none d-md-block" />
                  <span>
                    Mục tiêu scale:{' '}
                    <strong className="text-dark">{formatCurrency(result.adjustedTarget)}</strong>
                  </span>
                  <span className="vr d-none d-md-block" />
                  <span>
                    Biên độ scale:{' '}
                    <strong className="text-dark">{formatCurrency(result.adjustedTolerance)}</strong>
                  </span>
                  <span className="vr d-none d-md-block" />
                  <span>
                    PTTT:{' '}
                    <strong className="text-dark">
                      {PAYMENT_METHOD_LABELS[paymentMethod] || paymentMethod}
                    </strong>
                  </span>
                </div>
              </Card.Body>
            </Card>

            {/* Summary cards */}
            <Row className="g-3 mb-4">
              <Col xs={6} md={3}>
                <SummaryCard
                  title="Ngưỡng tối ưu"
                  value={result.threshold > 0 ? formatCurrency(result.threshold) : 'Không tìm được'}
                  subtitle={result.threshold > 0 ? `Chỉ tính đơn ≤ ${formatCurrency(result.threshold)}` : undefined}
                  variant="primary"
                />
              </Col>
              <Col xs={6} md={3}>
                <SummaryCard
                  title="Doanh thu TB / tháng"
                  value={formatCurrency(result.monthlyAverage)}
                  subtitle={`Tổng cả kỳ: ${formatCurrency(result.actualTotal)}`}
                  variant="info"
                />
              </Col>
              <Col xs={6} md={3}>
                <SummaryCard
                  title="Chênh lệch / tháng"
                  value={differenceLabel}
                  subtitle={`Mục tiêu: ${formatCurrency(result.targetAmount)}/tháng`}
                  variant={differenceColor}
                />
              </Col>
              <Col xs={6} md={3}>
                <SummaryCard
                  title="Số đơn trong ngưỡng"
                  value={formatNumber(result.orderCount)}
                  subtitle={`/ ${formatNumber(result.totalOrderCount)} đơn trong kỳ`}
                  variant="secondary"
                />
              </Col>
            </Row>

            {/* Steps table */}
            {result.steps?.length > 0 && (
              <Card className="mb-4">
                <Card.Header>
                  <strong>Các bước tìm ngưỡng</strong>
                  <span className="text-muted small ms-2">
                    (ngưỡng tối ưu được tô đậm)
                  </span>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table striped hover size="sm" className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Ngưỡng tiền đơn</th>
                          <th className="text-end">Tổng kỳ</th>
                          <th className="text-end">TB / tháng</th>
                          <th className="text-end">Số đơn</th>
                          <th className="text-center">Trạng thái</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.steps.map((step) => {
                          const isOptimal = step.threshold === result.threshold;
                          const withinTolerance =
                            step.sum <= result.adjustedTarget + result.adjustedTolerance;
                          return (
                            <tr
                              key={step.threshold}
                              className={isOptimal ? 'table-primary fw-bold' : ''}
                            >
                              <td>
                                {formatCurrency(step.threshold)}
                                {isOptimal && (
                                  <Badge bg="primary" className="ms-2">
                                    Tối ưu
                                  </Badge>
                                )}
                              </td>
                              <td className="text-end">{formatCurrency(step.sum)}</td>
                              <td className="text-end">{formatCurrency(step.monthlyAverage)}</td>
                              <td className="text-end">{formatNumber(step.orderCount)}</td>
                              <td className="text-center">
                                {withinTolerance ? (
                                  <Badge bg="success">✓ Đạt</Badge>
                                ) : (
                                  <Badge bg="danger">✗ Vượt</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Orders table */}
            {result.threshold > 0 && (
              <Card>
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong>
                      Danh sách đơn hàng dưới {formatCurrency(result.threshold)}
                    </strong>
                    {!loadingOrders && (
                      <span className="text-muted small ms-2">
                        ({formatNumber(ordersTotal)} đơn)
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleExportCsv}
                    disabled={exportingCsv || ordersTotal === 0}
                  >
                    {exportingCsv ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Đang xuất...
                      </>
                    ) : (
                      'Xuất CSV'
                    )}
                  </Button>
                </Card.Header>
                <Card.Body className="p-0">
                  {loadingOrders ? (
                    <div className="text-center py-4">
                      <Spinner />
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      Không có đơn hàng nào.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Mã đơn</th>
                            <th>Chi nhánh</th>
                            <th>Bàn</th>
                            <th className="text-end">Tổng tiền</th>
                            <th>PTTT</th>
                            <th>Hoàn thành lúc</th>
                            <th>Nhân viên</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map((order) => (
                            <tr key={order.id}>
                              <td>
                                <code className="small">{order.orderNumber}</code>
                              </td>
                              <td>{order.branchName || order.branchId}</td>
                              <td>{order.tableNumber ?? '-'}</td>
                              <td className="text-end fw-semibold">
                                {formatCurrency(order.totalAmount)}
                              </td>
                              <td>
                                <Badge bg="secondary" className="fw-normal">
                                  {PAYMENT_METHOD_LABELS[order.paymentMethod] ||
                                    order.paymentMethod}
                                </Badge>
                              </td>
                              <td className="text-nowrap">
                                {formatDateTime(order.completedAt)}
                              </td>
                              <td>{order.createdBy || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {ordersTotal > ordersLimit && (
                    <div className="p-3">
                      <PaginationComponent
                        totalItems={ordersTotal}
                        itemsPerPage={ordersLimit}
                        currentPage={ordersPage}
                        onPageChange={handlePageChange}
                        loading={loadingOrders}
                      />
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}
